import type { JourneyWithPassengerSegments } from "@repo/ui/types/extras";

/**
 * Checks if the 'inBundle' property is true for a given SSR code
 * within any journey in the provided array.
 * @param {Array<Journey>} journeys - The array of journey objects (optional).
 * @param {string} ssrCodeToCheck - The SSR code to check for (e.g., 'pet_on_board').
 * @returns {boolean} - True if isInBundle is true for at least one journey and SSR, otherwise false.
 */
export const isSsrInAnyJourneyInBundle = (
  journeys?: JourneyWithPassengerSegments[],
  ssrCodeToCheck?: string,
) =>
  journeys?.some((journey) => {
    const firstPassenger = Object.values(journey.passengerSegment)[0];
    return firstPassenger?.ssrs?.some(
      (ssr) => ssr.ssrCode.toLowerCase() === ssrCodeToCheck?.toLowerCase() && ssr.inBundle,
    );
  });

/**
 * Gets the maximum allowed selections for a specific SSR from the journey's legs availability.
 * @param {JourneyWithPassengerSegments} journey - The journey object.
 * @param {string[]} codesToCheck - The SSR codes to check for.
 * @param {number} defaultLimit - The fallback limit if no SSR limit is found.
 * @returns {number} - The minimum limit (lid) from the SSR availability across all legs.
 */
export const getJourneySsrMaxSelections = (
  journey: JourneyWithPassengerSegments,
  codesToCheck: string[],
  defaultLimit = 4,
): number => {
  if (!journey?.segments) return defaultLimit;

  for (const segment of journey.segments) {
    if (!segment.legs) continue;

    for (const leg of segment.legs) {
      if (!leg.ssrs) continue;

      for (const ssr of leg.ssrs) {
        if (codesToCheck.includes(ssr.ssrNestCode) && typeof ssr.lid === "number") {
          return ssr.lid;
        }
      }
    }
  }

  return defaultLimit;
};
